
import fb from './gobi-mw-firestore';
import store from '@/store';
import { ProgramData,  TypicalResponse, UpdateProgramSubmission, AddProgramSubmission, AccountMinimumData } from './models.def';
import programx from './modules/programx';
import uuid from 'uuid';
import userx from './modules/userx';
import { firestore } from 'firebase';


const db: firebase.firestore.Firestore = fb.db;
const functions: firebase.functions.Functions = fb.functions;

export class Programs {

	public db: firebase.firestore.Firestore | null = null;
	public functions: firebase.functions.Functions | null = null;

	private _unsubscribeListenToAllPrograms: (() => void) | null = null;

	constructor() {

		// auto init when user.supportLvl >= 1
		if (userx.supportLvl && userx.supportLvl >= 1) {
			this.init();
		}
		store.watch(
			(states, getters) => getters['userx/supportLvl'],
			(newValue, oldValue) => {
				if (!newValue) {
					if (this._unsubscribeListenToAllPrograms) {
						this._unsubscribeListenToAllPrograms();
						this._unsubscribeListenToAllPrograms = null;
					}
					programx.updateAllPrograms([]);
				} else if (newValue >= 1) {
					this.init();
				}
			});
	}


	public async init() {
		if (!this._unsubscribeListenToAllPrograms) {
			this.listenToAllProgramsSnapshop();
		}
	}

	public listenToAllProgramsSnapshop() {
		this._unsubscribeListenToAllPrograms = db.collection('programs').orderBy('createdAt', 'desc')
			.onSnapshot((docs) => {
				if (docs.empty) { return; }

				const allPrograms: ProgramData[] = [];
				docs.forEach((doc) => {
					const programData: ProgramData = doc.data() as ProgramData;
					for (const group of programData.groups) {
						group.target = group.target || 'path';
					}
					allPrograms.push(programData);
				});
				programx.updateAllPrograms(allPrograms);
			});
	}

	public async addProgram(submission: AddProgramSubmission): Promise<TypicalResponse> {
		const assert = fb.assertSupportLvl(10);
		if (!assert.success) { return assert; }

		const { id, softwareType } = submission;
		const createdAt = Date.now();
		const updatedAt = createdAt;

		const ref = db.doc(`programs/${id}`);
		if (programx.allPrograms.find((pg) => pg.id === id)) {
			return { success: false, errorCode: 'already-exists', errorMessage: 'Program ID already exists.' };
		}
		try {
			await ref.set({ id, softwareType, createdAt, updatedAt, groups: [], version: 0, fileFormat: 'both'});
			return { success: true };
		} catch (error: any) {
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}
	public async updateProgram(submission: UpdateProgramSubmission): Promise<TypicalResponse> {
		const assert = fb.assertSupportLvl(10);
		if (!assert.success) { return assert; }

		const currentProgram = programx.allPrograms.find((pg) => pg.id === submission.id);
		if (!currentProgram) {
			return { success: false, errorCode: 'not-found', errorMessage: 'Program Not Found.' };
		}
		const groupsChanged = JSON.stringify(currentProgram.groups) !== JSON.stringify(submission.groups);
		const fileFormatChanged = (currentProgram.fileFormat || 'both') !== (submission.fileFormat || 'both');
		if (!groupsChanged && !fileFormatChanged) {
			return { success: false, errorCode: 'not-changed', errorMessage: 'Program Not Changed.' };
		}
		submission.version = firestore.FieldValue.increment(1);
		submission.updatedAt = Date.now();
		const ref = db.doc(`programs/${submission.id}`);
		try {
			await ref.update(submission);
			// TODO: broadcast commands
			return { success: true };
		} catch (error: any) {
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}

	public async getAccountsUsingProgram(programId: string): Promise<AccountMinimumData[]> {
		try {
			const snapshot = await db.collection('accounts')
				.where('programId', '==', programId)
				.get();

			const accounts: AccountMinimumData[] = [];
			snapshot.forEach((doc) => {
				const data = doc.data();
				accounts.push({
					accountId: data.accountId,
					companyName: data.companyName,
					companyRemark: data.companyRemark || '',
					createdBy: data.createdBy,
					createdAt: data.createdAt,
					expiryTime: data.expiryTime || 0,
				});
			});
			return accounts;
		} catch (error: any) {
			console.error('Error fetching accounts using program:', error);
			return [];
		}
	}

	public async deleteProgram(programId: string): Promise<TypicalResponse> {
		const assert = fb.assertSupportLvl(10);
		if (!assert.success) { return assert; }

		// Check if any accounts are using this program
		const accountsUsing = await this.getAccountsUsingProgram(programId);
		if (accountsUsing.length > 0) {
			return {
				success: false,
				errorCode: 'in-use',
				errorMessage: `Cannot delete program. ${accountsUsing.length} account(s) are using it.`,
			};
		}

		const ref = db.doc(`programs/${programId}`);
		try {
			await ref.delete();
			return { success: true };
		} catch (error: any) {
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}
}

const programz = new Programs();
export default programz;
